import { useEffect, useState } from "react";
import { Gear, CyclicAction, BinaryAction } from "@components/operatorLobby/ControlStream";
import KeyboardSettings from "../shared/keyboardSettings/KeyboardSettings";

const KeyMapping = {
  Forward: "w",
  Backward: "s",
  Left: "a",
  Right: "d",
  IncreaseSpeed: "r",
  DecreaseSpeed: "f",
  IncreaseRpm: "Shift",
  DecreaseRpm: "Control",
  Brake: " ", // Space
  StartEngine: "g",
  Headlight: "l",
  Tool: "t",
  TranslationRight: "ArrowRight",
  TranslationLeft: "ArrowLeft",
  ArmUp: "ArrowUp",
  ArmDown: "ArrowDown",
  CoverFlailUp: "u",
  CoverFlailDown: "j",
  HydraulicAuxUp: "z",
  HydraulicAuxDown: "h",
  ElectricAux: "x",
  Enlargement: "n",
  Shrinking: "m",
  NeutralFlotting: "b",
  MaintenanceState: "1",
  ParkingState: "2",
  DockingState: "3",
  DrivingState: "4",
  AutonomousState: "5",
  Ignition: "p",
  EnableEmergencyStop: "Enter",
};

function camelCaseToNormalCase(text) {
  const result = text
    .replace(/([A-Z])/g, " $1")
    .trim()
    .toLowerCase();
  const finalResult = result.charAt(0).toUpperCase() + result.slice(1);
  return finalResult;
}

function help() {
  //   var rows = [];
  //   for (const key in KeyMapping) {
  //     const row = (
  //       <div className="col-lg-6" key={key}>
  //         <div className="row">
  //           <div className="col-6">
  //             <div className="bg-light">{camelCaseToNormalCase(key)}</div>
  //           </div>
  //           <div className="col-6">
  //             <div className="bg-light">
  //               {KeyMapping[key] === " " ? "Space" : camelCaseToNormalCase(KeyMapping[key])}
  //             </div>
  //           </div>
  //         </div>
  //       </div>
  //     );
  //     rows.push(row);
  //   }
  return <KeyboardSettings />;
}

export default function useKeyboard() {
  const [enable, setEnable] = useState(false);
  const [controlUpdate, setControlUpdate] = useState(null);
  const [controlSend, setControlSend] = useState(null);
  const [vehicleStateRequest, setVehicleStateRequest] = useState(null);

  useEffect(() => {
    if (enable && controlUpdate && controlSend && vehicleStateRequest) {
      let pressedKeys = new Set();
      let lastTime = null;
      //let animationRequestId = window.requestAnimationFrame(update);

      let linearSpeed = 0.0;
      let maxLinearSpeed = 1.5;
      let linearSpeedIncrement = 0.1;
      let angularSpeed = 0.0;
      let maxAngularSpeed = 6.0;
      let angularSpeedIncrement = 0.4;

      let engineRPM = 0;
      let engineRPMIncrement = 10;

      function decay(value, step, deltaTime) {
        let sign = Math.sign(value);
        value -= sign * step * deltaTime;
        return Math.sign(value) !== sign ? 0.0 : value;
      }

      function apply(value, step, extreme, deltaTime) {
        value += step * deltaTime;
        return Math.abs(value) > Math.abs(extreme) ? extreme : value;
      }

      function onKeyDown(event) {
        if (event.repeat) return;

        const key = event.key;
        pressedKeys.add(key);

        switch (key) {
          case KeyMapping.Forward:
            controlUpdate((control) => ({ ...control, linearSpeed: Math.abs(1.0) }));
            break;
          case KeyMapping.Backward:
            controlUpdate((control) => ({ ...control, linearSpeed: -Math.abs(1.0) }));
            break;
          case KeyMapping.Right:
            controlUpdate((control) => ({ ...control, angularSpeed: -Math.abs(1.0) }));
            break;
          case KeyMapping.Left:
            controlUpdate((control) => ({ ...control, angularSpeed: Math.abs(1.0) }));
            break;
          case KeyMapping.IncreaseSpeed:
            controlUpdate((control) => ({ ...control, increaseLinearSpeed: BinaryAction.Flip }));
            controlUpdate((control) => ({ ...control, increaseAngularSpeed: BinaryAction.Flip }));
            break;

          case KeyMapping.DecreaseSpeed:
            controlUpdate((control) => ({ ...control, decreaseLinearSpeed: BinaryAction.Flip }));
            controlUpdate((control) => ({ ...control, decreaseAngularSpeed: BinaryAction.Flip }));
            break;

          case KeyMapping.IncreaseRpm:
            controlUpdate((control) => ({ ...control, increaseRPM: BinaryAction.Flip }));
            break;

          case KeyMapping.DecreaseRpm:
            controlUpdate((control) => ({ ...control, decreaseRPM: BinaryAction.Flip }));
            break;
          case KeyMapping.Brake:
            break;
          case KeyMapping.EnableEmergencyStop:
            vehicleStateRequest("emergency");
            break;
          case KeyMapping.Ignition:
            controlUpdate((control) => ({ ...control, ignition: BinaryAction.Flip }));
            break;
          case KeyMapping.ElectricAux:
            controlUpdate((control) => ({ ...control, electricAux: BinaryAction.Flip }));
            break;
          case KeyMapping.Headlight:
            controlUpdate((control) => ({ ...control, headlight: BinaryAction.Flip }));
            break;
          case KeyMapping.Enlargement:
            controlUpdate((control) => ({ ...control, enlargementSpeed: Math.abs(1.0) }));
            break;
          case KeyMapping.Shrinking:
            controlUpdate((control) => ({ ...control, enlargementSpeed: -Math.abs(1.0) }));
            break;
          case KeyMapping.NeutralFlotting:
            controlUpdate((control) => ({ ...control, neutralFlotting: true }));
            break;
          case KeyMapping.Tool:
            controlUpdate((control) => ({ ...control, tool: true }));
            break;
          case KeyMapping.CoverFlailUp:
            controlUpdate((control) => ({ ...control, coverFlailUpDownSpeed: Math.abs(1.0) }));
            break;
          case KeyMapping.CoverFlailDown:
            controlUpdate((control) => ({ ...control, coverFlailUpDownSpeed: -Math.abs(1.0) }));
            break;
          case KeyMapping.HydraulicAuxUp:
            controlUpdate((control) => ({ ...control, hydraulicAuxSpeed: Math.abs(1.0) }));
            break;
          case KeyMapping.HydraulicAuxDown:
            controlUpdate((control) => ({ ...control, hydraulicAuxSpeed: -Math.abs(1.0) }));
            break;
          case KeyMapping.ArmUp:
            controlUpdate((control) => ({ ...control, armUpDownSpeed: Math.abs(1.0) }));
            break;
          case KeyMapping.ArmDown:
            controlUpdate((control) => ({ ...control, armUpDownSpeed: -Math.abs(1.0) }));
            break;
          case KeyMapping.TranslationLeft:
            controlUpdate((control) => ({ ...control, translationLeftRightSpeed: -Math.abs(1.0) }));
            break;
          case KeyMapping.TranslationRight:
            controlUpdate((control) => ({ ...control, translationLeftRightSpeed: Math.abs(1.0) }));
            break;

          case KeyMapping.StartEngine:
            controlUpdate((control) => ({ ...control, engine: BinaryAction.Set }));
            break;
          case KeyMapping.MaintenanceState:
            vehicleStateRequest("maintenance");
            break;
          case KeyMapping.ParkingState:
            vehicleStateRequest("parking");
            break;
          case KeyMapping.DockingState:
            vehicleStateRequest("docking");
            break;
          case KeyMapping.DrivingState:
            vehicleStateRequest("driving");
            break;
          case KeyMapping.AutonomousState:
            vehicleStateRequest("autonomous");
            break;
          default:
            break;
        }
        controlSend();
      }

      function onKeyUp(event) {
        const key = event.key;
        pressedKeys.delete(key);
        switch (key) {
          case KeyMapping.Forward:
            controlUpdate((control) => ({ ...control, linearSpeed: 0 }));
            break;
          case KeyMapping.Backward:
            controlUpdate((control) => ({ ...control, linearSpeed: 0 }));
            break;
          case KeyMapping.Right:
            controlUpdate((control) => ({ ...control, angularSpeed: 0 }));
            break;
          case KeyMapping.Left:
            controlUpdate((control) => ({ ...control, angularSpeed: 0 }));
            break;
          case KeyMapping.StartEngine:
            controlUpdate((control) => ({ ...control, engine: BinaryAction.Clear }));
            break;

          case KeyMapping.ArmUp:
            controlUpdate((control) => ({ ...control, armUpDownSpeed: 0.0 }));
            break;
          case KeyMapping.ArmDown:
            controlUpdate((control) => ({ ...control, armUpDownSpeed: 0.0 }));
            break;
          case KeyMapping.TranslationLeft:
            controlUpdate((control) => ({ ...control, translationLeftRightSpeed: 0.0 }));
            break;
          case KeyMapping.TranslationRight:
            controlUpdate((control) => ({ ...control, translationLeftRightSpeed: 0.0 }));
            break;
          case KeyMapping.CoverFlailUp:
            controlUpdate((control) => ({ ...control, coverFlailUpDownSpeed: 0.0 }));
            break;
          case KeyMapping.CoverFlailDown:
            controlUpdate((control) => ({ ...control, coverFlailUpDownSpeed: 0.0 }));
            break;
          case KeyMapping.HydraulicAuxUp:
            controlUpdate((control) => ({ ...control, hydraulicAuxSpeed: 0.0 }));
            break;
          case KeyMapping.HydraulicAuxDown:
            controlUpdate((control) => ({ ...control, hydraulicAuxSpeed: 0.0 }));
            break;
          case KeyMapping.Enlargement:
            controlUpdate((control) => ({ ...control, enlargementSpeed: 0.0 }));
            break;
          case KeyMapping.Shrinking:
            controlUpdate((control) => ({ ...control, enlargementSpeed: 0.0 }));
            break;
          case KeyMapping.NeutralFlotting:
            controlUpdate((control) => ({ ...control, neutralFlotting: false }));
            break;
          case KeyMapping.Tool:
            controlUpdate((control) => ({ ...control, tool: false }));
            break;
          default:
            break;
        }
        controlSend();
      }

      function decreaseRPM() {
        engineRPM = engineRPM - engineRPMIncrement;
        if (engineRPM < 0.0) {
          engineRPM = 0.0;
        }
        controlUpdate((control) => ({ ...control, engineSpeed: Math.abs(engineRPM) }));
      }

      document.addEventListener("keydown", onKeyDown);
      document.addEventListener("keyup", onKeyUp);

      return () => {
        document.removeEventListener("keydown", onKeyDown);
        document.removeEventListener("keyup", onKeyUp);
        /*
                if(animationRequestId !== 0) {
                    window.cancelAnimationFrame(animationRequestId);
                }
                */
      };
    }
  }, [enable, controlUpdate, controlSend, vehicleStateRequest]);

  return { enable, setEnable, setControlUpdate, setControlSend, setVehicleStateRequest, help };
}
